import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Download, Loader2, X } from 'lucide-react';
import type { PropertyCutRender, RenderFormat } from '../../types';
import { cancelStalledRender, computeRenderCost, createRender } from '../../services/renders';
import { downloadRenderFile, fetchRenders } from '../../services/supabase';
import { meaningfulProgress, phaseForRender, phaseLabel, type ExportPhase } from '../../services/exportPhase';

interface ExportSheetProps {
  projectId: string;
  userId: string;
  format: RenderFormat;
  credits: number;
  blockedReason: string | null;
  onBeforeExport: () => Promise<void>;
  onCredits: (credits: number) => void;
  onClose: () => void;
}

function newExportKey(): string {
  const random = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `export:${random}`;
}

export default function ExportSheet(props: ExportSheetProps) {
  const [renders, setRenders] = useState<PropertyCutRender[] | null>(null);
  const [localPhase, setLocalPhase] = useState<ExportPhase | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [confirming, setConfirming] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const busy = useRef(false);
  // One key per export the user asked for: a resend after a lost response replays
  // the same charge instead of creating a second export.
  const exportKey = useRef<string | null>(null);

  const cost = computeRenderCost([props.format]);
  const blockedReason = props.blockedReason;

  const refresh = useCallback(async () => {
    const rows = await fetchRenders(props.projectId);
    setRenders(rows);
    return rows;
  }, [props.projectId]);

  useEffect(() => {
    void refresh();
    const poll = window.setInterval(() => void refresh(), 2000);
    const clock = window.setInterval(() => setNow(Date.now()), 1000);
    return () => {
      window.clearInterval(poll);
      window.clearInterval(clock);
    };
  }, [refresh]);

  const active = renders?.find((row) => row.id === activeId)
    ?? renders?.find((row) => row.status === 'queued' || row.status === 'processing')
    ?? null;
  const latest = active ?? renders?.[0] ?? null;
  const remotePhase = latest ? phaseForRender(latest, now) : null;
  const phase: ExportPhase | null = localPhase === 'preparing' ? 'preparing' : remotePhase;
  const inFlight = phase === 'preparing' || phase === 'queued' || phase === 'rendering';
  const progress = latest && phase ? meaningfulProgress(latest, phase) : null;

  const start = async () => {
    if (busy.current || blockedReason) return;
    busy.current = true;
    setError(null);
    setConfirming(false);
    setLocalPhase('preparing');
    exportKey.current ??= newExportKey();
    try {
      await props.onBeforeExport();
      const result = await createRender({
        projectId: props.projectId,
        userId: props.userId,
        formats: [props.format],
        cost,
        idempotencyKey: exportKey.current,
      });
      if (!result.success) {
        // A definite refusal: nothing was charged, so the next attempt is a new export.
        if (result.error !== 'CHARGE_FAILED') exportKey.current = null;
        setLocalPhase('failed');
        setError(result.errorMessage || 'Could not start the export.');
        return;
      }
      exportKey.current = null;
      setActiveId(result.renders[0]?.id ?? null);
      setLocalPhase(null);
      await refresh();
      props.onCredits(result.creditsRemaining ?? Math.max(0, props.credits - cost.total));
    } catch (err: any) {
      setLocalPhase('failed');
      setError(err?.message || 'Could not start the export.');
    } finally {
      busy.current = false;
    }
  };

  const retry = async () => {
    if (busy.current || !latest) return;
    busy.current = true;
    setError(null);
    setLocalPhase('preparing');
    try {
      const fresh = await refresh();
      const current = fresh.find((row) => row.id === latest.id) ?? latest;
      if (current.status === 'completed') {
        setLocalPhase(null);
        setActiveId(current.id);
        return;
      }
      const currentPhase = phaseForRender(current, Date.now());
      if ((current.status === 'queued' || current.status === 'processing') && currentPhase !== 'stalled') {
        setLocalPhase(null);
        setActiveId(current.id);
        return;
      }
      // The server fails and refunds a genuinely stuck export once; a failed
      // export was already refunded by the worker.
      const cancelled = await cancelStalledRender(current.id);
      if (!cancelled.success && cancelled.error === 'NOT_STALLED') {
        setLocalPhase(null);
        setActiveId(current.id);
        return;
      }
      if (!cancelled.success) {
        setLocalPhase('failed');
        setError('Could not confirm the previous charge was refunded, so retry was not submitted.');
        return;
      }
      busy.current = false;
      await start();
    } catch (err: any) {
      setLocalPhase('failed');
      setError(err?.message || 'Retry failed.');
    } finally {
      busy.current = false;
    }
  };

  const insufficient = cost.total > props.credits;

  const download = async () => {
    if (downloading || !latest?.output_url) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadRenderFile({
        outputPath: latest.output_path,
        outputUrl: latest.output_url,
        filename: `propertycut-${props.format}.mp4`,
      });
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fixed inset-y-0 right-0 z-40 w-full max-w-md h-dvh max-h-dvh bg-slate-950 border-l border-slate-800 shadow-2xl flex flex-col pb-[env(safe-area-inset-bottom)]">
      <header className="h-14 px-4 flex items-center justify-between border-b border-slate-800">
        <h2 className="text-sm font-medium">Export video</h2>
        <button type="button" onClick={props.onClose} className="p-1 text-slate-400 hover:text-white" aria-label="Close export">
          <X className="w-4 h-4" />
        </button>
      </header>
      <div className="p-4 space-y-4 overflow-y-auto">
        <p className="text-sm text-slate-300">
          MP4 · {props.format === 'vertical' ? '1080 × 1920' : props.format === 'square' ? '1080 × 1080' : '1920 × 1080'}
        </p>

        <p className="text-sm text-slate-200">
          This export uses <strong>{cost.total} credits</strong>. You have {props.credits}.
        </p>

        {blockedReason && <p className="text-sm text-amber-200">{blockedReason}</p>}
        {insufficient && !inFlight && (
          <p className="text-sm text-amber-200">Not enough credits ({props.credits} / {cost.total}).</p>
        )}

        {renders !== null && phase == null && !confirming && (
          <button
            type="button"
            onClick={() => void start()}
            disabled={Boolean(blockedReason) || insufficient || inFlight}
            className="w-full rounded-lg bg-teal-400 text-slate-950 font-medium py-2.5 disabled:bg-slate-800 disabled:text-slate-500"
          >
            Export video · {cost.total} credits
          </button>
        )}

        {phase && phase !== 'preparing' && latest && (
          <StatusBlock phase={phase} progress={progress} error={latest.error_message} />
        )}
        {phase === 'preparing' && <StatusBlock phase="preparing" progress={null} error={null} />}
        {error && <p className="text-sm text-red-300">{error}</p>}

        {phase === 'complete' && latest?.output_url && (
          <div className="space-y-3">
            <video src={latest.output_url} controls className="w-full rounded-lg bg-black" />
            <button
              type="button"
              onClick={() => void download()}
              disabled={downloading}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-white text-slate-950 px-3 py-2.5 text-sm font-medium disabled:opacity-60"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {downloading ? 'Downloading...' : 'Download MP4'}
            </button>
            {downloadError && <p className="text-sm text-red-300">{downloadError}</p>}
            <ReexportButton
              confirming={confirming}
              cost={cost.total}
              disabled={Boolean(blockedReason) || insufficient || inFlight}
              onAsk={() => setConfirming(true)}
              onCancel={() => setConfirming(false)}
              onStart={() => void start()}
            />
          </div>
        )}
        {phase === 'complete' && latest && !latest.output_url && (
          <div className="space-y-2">
            <p className="text-sm text-amber-200">The video finished, but the download link is missing.</p>
            <ReexportButton
              confirming={confirming}
              cost={cost.total}
              disabled={Boolean(blockedReason) || insufficient || inFlight}
              onAsk={() => setConfirming(true)}
              onCancel={() => setConfirming(false)}
              onStart={() => void start()}
            />
          </div>
        )}

        {(phase === 'failed' || phase === 'stalled') && (
          <div className="space-y-2">
            <p className="text-sm text-red-200">
              {phase === 'stalled'
                ? 'This export stopped making progress. Your project is still here.'
                : latest?.error_message || error || 'The export failed. Your project is still here.'}
            </p>
            <button
              type="button"
              onClick={() => void retry()}
              className="rounded-lg bg-white text-slate-950 px-3 py-2 text-sm font-medium"
            >
              Retry
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ReexportButton({
  confirming,
  cost,
  disabled,
  onAsk,
  onCancel,
  onStart,
}: {
  confirming: boolean;
  cost: number;
  disabled: boolean;
  onAsk: () => void;
  onCancel: () => void;
  onStart: () => void;
}) {
  if (!confirming) {
    return (
      <button type="button" onClick={onAsk} className="w-full rounded-lg border border-slate-700 py-2 text-sm text-slate-300">
        Export again
      </button>
    );
  }
  return (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={onStart}
        disabled={disabled}
        className="flex-1 rounded-lg bg-teal-400 text-slate-950 font-medium py-2.5 disabled:bg-slate-800 disabled:text-slate-500"
      >
        Export video · {cost} credits
      </button>
      <button type="button" onClick={onCancel} className="rounded-lg border border-slate-700 px-3 text-sm text-slate-300">
        Cancel
      </button>
    </div>
  );
}

function StatusBlock({
  phase,
  progress,
  error,
}: {
  phase: ExportPhase;
  progress: number | null;
  error: string | null;
}) {
  const spinning = phase === 'preparing' || phase === 'queued' || phase === 'rendering';
  return (
    <div className="rounded-lg border border-slate-800 px-3 py-3">
      <p className="text-sm flex items-center gap-2">
        {spinning && <Loader2 className="w-4 h-4 animate-spin text-teal-300" />}
        <span>{phaseLabel(phase)}{progress != null ? ` · ${progress}%` : ''}</span>
      </p>
      {phase === 'queued' && <p className="text-xs text-slate-500 mt-1">Waiting for the render worker.</p>}
      {phase === 'failed' && error && <p className="text-xs text-red-300 mt-2">{error}</p>}
    </div>
  );
}
